import { Component, OnInit, OnDestroy, ViewContainerRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { map, filter, takeUntil, withLatestFrom, first, pairwise, startWith, distinctUntilChanged } from 'rxjs/operators';
import { AlgOtpModalComponent } from '../../components/dialogs/alg-otp-modal/alg-otp-modal.component';

import * as moment from 'moment';
import 'moment/locale/it';

import { ModalDialogService, SimpleModalComponent, IModalDialogButton } from 'ngx-modal-dialog';
import { UtilsService } from '../../services/utils.service';
import { AlgConditionModalComponent } from '../../components/dialogs/alg-condition-modal/alg-condition-modal.component';
import { Condition } from '../../models/contitions';

import { environment } from '../../../environments/environment';
import {
  AlgFisicoService,
  APPROVAL_STATUS_OK,
  APPROVAL_STATUS_SIGNED,
  APPROVAL_STATUS_INVALID,
  APPROVAL_STATUS_ERROR,
  APPROVAL_STATUS_COMPLETED,
} from '../../services/alg-fisico.service';
import {
  PhysicApprovalConsents,
  PhysicApprovalFeedback
} from '../../services/alg-fisico.service.d';

@Component({
  selector: 'alg-fisico',
  templateUrl: './alg-fisico.component.html',
  styleUrls: ['./alg-fisico.component.scss']
})
export class AlgFisicoComponent implements OnInit, OnDestroy {

  public isLeadLoading$: Observable<boolean>;
  public isPhysicApprovalOk$: Observable<boolean>;
  public isPhysicApprovalCompleted$: Observable<boolean>;
  public isErrorAlreadySigned$: Observable<boolean>;
  public isErrorInvalidOffer$: Observable<boolean>;
  public isErrorGeneric$: Observable<boolean>;
  public productType$: Observable<string>;
  public operatorRating$: Observable<PhysicApprovalFeedback>;
  public isConsentsOk: boolean;
  public feedbackOptions = [
    {image: 'bad', value: 100},
    {image: 'normal', value: 200},
    {image: 'good', value: 300},
    {image: 'excellent', value: 400}
  ];
  public feedbackModalContent = {
    title: 'La tua privacy per noi è importante.',
    description: ''
  };
  private leadId$: Observable<any>;
  private leadId: string;
  private privacyLinkUrl: string;
  private physicApprovalId: string;
  private phone_number: string;
  private consents: PhysicApprovalConsents;
  private ngUnsubscribe = new Subject<void>();

  constructor(
    private activatedRoute: ActivatedRoute,
    private consensoService: AlgFisicoService,
    private utils: UtilsService,
    private modalDialogService: ModalDialogService,
    private viewContainer: ViewContainerRef
  ) {
    this.leadId$ = this.activatedRoute.params
      .pipe(
        map(params => params && params.id)
      );
    this.isLeadLoading$ = this.consensoService.isLeadLoading$;
    this.isPhysicApprovalOk$ = this.consensoService.leadStatus$
    .pipe(
      map((status) => status === APPROVAL_STATUS_OK),
      startWith(false),
      distinctUntilChanged()
    );
    this.isPhysicApprovalCompleted$ = this.consensoService.leadStatus$
      .pipe(
        map((status) => status === APPROVAL_STATUS_COMPLETED),
        startWith(false),
        distinctUntilChanged()
      );
    this.isErrorAlreadySigned$ = this.consensoService.leadStatus$
      .pipe(
        map((status) => status === APPROVAL_STATUS_SIGNED),
        startWith(false),
        distinctUntilChanged()
      );
    this.isErrorInvalidOffer$ = this.consensoService.leadStatus$
      .pipe(
        map((status) => status === APPROVAL_STATUS_INVALID),
        startWith(false),
        distinctUntilChanged()
      );
    this.isErrorGeneric$ = this.consensoService.leadStatus$
      .pipe(
        map((status) => status === APPROVAL_STATUS_ERROR),
        startWith(false),
        distinctUntilChanged()
      );
    this.productType$ = this.consensoService.leadProduct$
      .pipe(
        map((product) => !!product && product.type),
      );
    this.operatorRating$ = this.consensoService.operatorRatingCode$
      .pipe(
        first((rating) => !!rating)
      );
  }

  public ngOnInit() {
    // SUBSCRIBE: Redirect if no lead ID in URL param
    this.leadId$
      .pipe(
        filter(id => typeof id !== 'string' || typeof id === 'string' && id.trim() === ''),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((id) => {
        window.location.href = environment.websiteUrl;
      });
    // SUBSCRIBE: Fetch lead ID taken from URL param
    this.leadId$
      .pipe(
        filter(id => typeof id === 'string' && id.trim() !== ''),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((id) => {      
        this.consensoService.fetchLead(id);
      });
    // SUBSCRIBE: Keep instance copy of lead and physicApproval ids
    this.consensoService.lead$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((physicApproval) => {
          this.physicApprovalId = !!physicApproval && physicApproval.id;
          this.leadId = !!physicApproval && physicApproval.leadId;
          this.phone_number = !!physicApproval && physicApproval.phone;
        });
    // SUBSCRIBE: Keep instance copy of consent validity status and consents object
    this.consensoService.isConsentsOk$
      .pipe(
        withLatestFrom(
          this.consensoService.privacyConsets$,
        ),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(([isconsentsOk, privacyConsents]) => {
        this.isConsentsOk = isconsentsOk;
        this.consents = {
          ...privacyConsents,
        };
      });
    // SUBSCRIBE: save and prepare policies links
    this.consensoService.policiesUrl$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((policiesUrls) => {
        this.feedbackModalContent = {
          ...this.feedbackModalContent,
          description: `Effettuando la valutazione della chiamata esprimi il consenso al trattamento dei tuoi dati ai fini della valutazione della qualità del servizio. <a href="${policiesUrls.generalPolicy}" target="_blank">Info privacy</a>`
        };
      });
    // SUBSCRIBE: scroll to top when physicApproval status goes to APPROVAL_STATUS_COMPLETED
    this.isPhysicApprovalCompleted$
      .pipe(
        pairwise(),
        takeUntil(this.ngUnsubscribe),
        filter(([oldStatus, newStatus]) => !oldStatus && newStatus === true)
      )
      .subscribe(() => {
        if (window.scrollTo) {
          window.scrollTo(0, 0);
        }
      });
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public openModal(modalContent) {
    this.utils.disableOverflow();
    this.modalDialogService.openDialog(this.viewContainer, {
      title: modalContent.title,
      childComponent: AlgConditionModalComponent,
      settings: {
        closeButtonClass: 'close theme-icon-close',
        headerTitleClass: ' modal-title modal-title-underline'
      },
      data: {
        description: modalContent.description
      }
    });
  }

  public readCondition(type) {
    let modalContent;
    if (type === 'dataConsent') {
      modalContent = this.feedbackModalContent;
    }
    this.openModal(modalContent || {});
  }

  // feedback
  public onFeebackModal(event) {
    if (event === 'open') {
      this.readCondition('dataConsent');
    }
  }

  // TODO: Rename this method
  public onFeedback(feedback) {
    this.consensoService.saveFeedback(
      this.physicApprovalId,
      feedback
    );
  }

  public nextStep() {
    if (this.isConsentsOk) {
      this.consensoService
        .saveConsents(this.leadId, this.consents, false);
    }
  }


  public openOTPmodal(){
    if(!this.isConsentsOk) return 
    this.utils.disableOverflow();
    this.modalDialogService.openDialog(this.viewContainer, {
      title: 'Un ultimo passo: verifica il tuo numero di cellulare',
      childComponent: AlgOtpModalComponent,
      settings: {
        closeButtonClass: 'close theme-icon-close',
        headerTitleClass: 'modal-title modal-title-underline textCenter'
      },
      data: {
        doneFunction: this.nextStep,
        isConsentsOk: this.isConsentsOk,
        consents: this.consents,
        phone: this.phone_number,
      }
    });
  }
  
}



// WEBPACK FOOTER //
// ./src/app/pages/alg-fisico/alg-fisico.component.ts